import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCloseOnScroll } from '../../hooks/useCloseOnScroll';
import { useLayoutPosition } from '../../hooks/useLayoutPosition';
import { Overlay } from '../Overlay/Overlay';

import { SelectSkeletonContext } from './context';
import { useMenuPosition } from './hooks/useMenuPosition';
import { SelectSkeletonProps, SELECT_SKELETON_MENU_POSITIONS } from './SelectSkeleton.decl';
import { SelectSkeletonContainer } from './SelectSkeletonContainer';
import { SelectSkeletonMenu } from './SelectSkeletonMenu';

import { AnimatePresence } from 'framer-motion';
import noop from 'lodash-es/noop';

/** Minimum distance required between <SelectSkeleton> and the bottom of the outer scrollable container */
const EMPTY_VALUE = { value: '', label: '' };
function DefaultMenuComponent({ children }: { children: React.ReactNode }) {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

export const SelectSkeleton = React.memo(
  ({
    inputComponent: InputComponent,
    menuComponent: MenuComponent = DefaultMenuComponent,
    optionRenderer,
    valueRenderer,
    defaultSelected,
    onChange,
    onBlur = noop,
    onClose = noop,
    options,
    label,
    name,
    disabled = false,
    hideChevron,
    placeholder,
    backgroundColor = '#FFF',
    icon,
    size = 'regular',
    menuPosition,
    disableItemAutofocus,
    'data-test': dataTest = 'select',
    usePortal = true,
    maxHeight = 200,
    ...boxProps
  }: SelectSkeletonProps) => {
    const initialValue = useMemo(
      () => options.find((option) => option.value === defaultSelected) || EMPTY_VALUE,
      [defaultSelected, options]
    );

    const [selected, setSelected] = useState(initialValue);
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    /** Stores the bounding rect of <SelectContainer> */
    const { boundingRect: selectPos, updateLayoutPosition } = useLayoutPosition(containerRef);

    const closeMenu = useCallback(() => {
      setOpen(false);
      onClose();
    }, [onClose]);

    const { outerScrollableEle } = useCloseOnScroll({
      eleRef: containerRef,
      innerScrollRef: menuRef,
      open,
      // closes the select menu when scrolling outside the menu
      // to prevent the menu from appearing in the incorrect position.
      onClose: closeMenu,
    });

    // If <SelectSkeleton> is in the bottom of the scrollable container/page,
    // shows the menu on the top.
    const computedMenuPos = useMenuPosition({
      menuPosition,
      containerRef,
      outerScrollableEle,
      minBottomOffset: maxHeight + 20,
    });

    const openMenu = useCallback(() => {
      if (!disabled) {
        setOpen(true);

        // update <SelectSkeleton>'s position to prevent it from not being calculated correctly
        // because it is within a framer-motion's component, e.g., modal
        updateLayoutPosition();
      }
    }, [disabled, updateLayoutPosition]);

    const toggleMenu = useCallback(() => {
      open ? closeMenu() : openMenu();
    }, [open, openMenu, closeMenu]);

    const dropdownPos = useMemo(() => {
      if (!selectPos) {
        return {
          width: 0,
          top: 0,
          left: 0,
        };
      }
      if (computedMenuPos === SELECT_SKELETON_MENU_POSITIONS.TOP) {
        return {
          width: selectPos.width,
          top: selectPos.top + 1,
          left: selectPos.left,
        };
      }

      return {
        width: selectPos.width,
        top: selectPos.bottom - 1,
        left: selectPos.left,
      };
    }, [computedMenuPos, selectPos]);

    useEffect(() => {
      const handleRootClose = (e: MouseEvent) => {
        if (
          open &&
          !containerRef.current!.contains(e.target as Node) &&
          !menuRef.current!.contains(e.target as Node)
        ) {
          closeMenu();
        }
      };

      const handleEscape = (e: KeyboardEvent) => {
        if (open && e.key === 'Escape') {
          closeMenu();
        }
      };

      document.addEventListener('click', handleRootClose, true);
      document.addEventListener('keyup', handleEscape, true);

      return () => {
        document.removeEventListener('click', handleRootClose, true);
        document.removeEventListener('keyup', handleEscape, true);
      };
    }, [setOpen, open, closeMenu]);

    // This is necessary in case we have options that are dynamic such as coming from the backend
    // mind that the options when passed to the select component should be memoized too in order
    // to not cause multiple runs of the useEffect hook which would make the initialValue being overriden in every run
    useEffect(() => {
      setSelected(initialValue);
    }, [initialValue, setSelected]);

    const selectSkeletonContext = useMemo(
      () => ({
        selected,
        setSelected,
        onChange,
        optionRenderer,
        toggleMenu,
        openMenu,
        closeMenu,
        size,
      }),
      [selected, setSelected, onChange, optionRenderer, toggleMenu, openMenu, closeMenu, size]
    );

    const skeletonMenu = (
      <SelectSkeletonMenu
        menuComponent={MenuComponent}
        ref={menuRef}
        options={options}
        backgroundColor={backgroundColor}
        menuPosition={computedMenuPos}
        disableItemAutofocus={disableItemAutofocus}
        data-test={dataTest}
        defaultSelected={defaultSelected}
        onChange={onChange}
        label={label}
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        size={size}
        optionRenderer={optionRenderer}
        isOpen={open}
        selectedOption={selected}
        maxHeight={maxHeight}
        {...dropdownPos}
      />
    );

    const menuContainer = usePortal ? <Overlay>{skeletonMenu}</Overlay> : skeletonMenu;

    return (
      <SelectSkeletonContext.Provider value={selectSkeletonContext}>
        <SelectSkeletonContainer
          label={label}
          name={name}
          disabled={disabled}
          data-test={dataTest}
          ref={containerRef}
          isOpen={open}
          menuPosition={computedMenuPos}
          hideChevron={!!hideChevron}
          backgroundColor={backgroundColor}
          size={size}
          {...boxProps}
        >
          <InputComponent
            defaultSelected={defaultSelected}
            onBlur={onBlur}
            onChange={onChange}
            options={options}
            label={label}
            name={name}
            disabled={disabled}
            placeholder={placeholder}
            backgroundColor={backgroundColor}
            icon={icon}
            size={size}
            menuPosition={computedMenuPos}
            data-test={dataTest}
            valueRenderer={valueRenderer}
            isOpen={open}
            selectedOption={selected}
          />
        </SelectSkeletonContainer>
        <AnimatePresence>{open && menuContainer}</AnimatePresence>
      </SelectSkeletonContext.Provider>
    );
  }
);
